/**
 * CanonicalFactStore — the single authoritative, tier-reconciled value for
 * every logical fact a report narrative is allowed to assert.
 *
 * ─── Why ──────────────────────────────────────────────────────────────
 *
 * Report-gen quality remediation P1 ("grounded content", tidy-sniffing-teacup
 * rev9). The audit found the SED carries the SAME logical fact in several
 * section-scoped slots (power declared in projectIntent AND power semantics;
 * the electric utility in grid AND siteResearch; site acreage in parcel AND
 * siteProfile; the ISO region in grid AND firmCapacity; …). Because the
 * generic registry walkers key by `<sectionId>.<field>`, those slots never
 * compete — so the prompt can (and does) surface two different numbers for
 * one fact, and the LLM picks one ungrounded.
 *
 * The CanonicalFactStore collapses each such logical fact into ONE
 * `factId`, reconciles its candidate sources through the EXISTING RDX tier
 * ladder (`reconcileTierSources`, tier-1 provider/user wins), and records a
 * HONEST `FactStatus` — `verified` only for a genuine tier-1 provider/user
 * source, `estimated` for tier-2/3 or derived, `labeled-unverifiable` for a
 * value that exists but no source can confirm (degraded/non-US coverage),
 * and `absent` for "no value at any tier — do NOT invent". Conflicts are
 * SURFACED in `unresolvedConflicts`, never silently dropped.
 *
 * ─── Discipline ───────────────────────────────────────────────────────
 *
 *  • PURE TYPES + a pure store shape. No I/O, no Firestore, no DOM. The
 *    builder (`buildCanonicalFactStore.ts`) is likewise pure.
 *  • Reuses the resolver-layer primitives verbatim (`SourceRef`,
 *    `ConflictDetail`, `ResolverTier`) — this module does NOT invent a
 *    parallel provenance vocabulary.
 *  • Flag-gated downstream: nothing here is constructed unless
 *    `reportGroundingMode() !== 'off'`. With the flag OFF the report is
 *    byte-identical to today.
 */

import type { ConflictDetail, ResolverTier, SourceRef } from '../resolvers/types';

/**
 * The honesty grade of a canonical fact.
 *
 *  - `'verified'`            — the winning source is a genuine tier-1
 *    provider / user / report-override / site-canonical / manual entry, on a
 *    site whose coverage is NOT degraded. Safe to assert as fact.
 *  - `'estimated'`           — the winning source is tier-2/tier-3 (site
 *    context, LLM inference, prior-report derived) OR a derived value (e.g.
 *    IT-load from declared ÷ PUE). MUST be labelled as an estimate in prose.
 *  - `'labeled-unverifiable'`— a value is present but no source can confirm
 *    it: the site is in a degraded / non-US coverage mode and the value's
 *    only sources are the skipped US-only provider classes. This is the
 *    provenance-laundering guard — an AI-researched non-US utility name is
 *    NOT `verified`. MUST be labelled "unverified" in prose.
 *  - `'absent'`              — no value at ANY tier. The narrative MUST NOT
 *    state a value; it must say the fact is not established.
 */
export type FactStatus = 'verified' | 'estimated' | 'labeled-unverifiable' | 'absent';

/**
 * Optional role scope for a numeric fact that is valid only in a named,
 * deterministic calculation context.
 *
 * Phase-feasibility rows contain several different MW quantities side by
 * side (facility load, required firm capacity, firm-deliverable capacity,
 * and per-source nameplate / firm MW).  Putting those values into the global
 * MW allow-set without their role would let unrelated prose borrow one of the
 * numbers.  The validator therefore requires the report line to name both the
 * phase and the matching metric before this fact can ground a mention.
 */
export type NumericGroundingScope =
  | {
      /** Conditional demand arithmetic from a frozen economic screening case. */
      kind: 'screening-load';
      phaseSequence?: number;
      inputBasis: 'facility' | 'it';
      metric: 'facility' | 'it';
    }
  | {
      /** Planned cumulative capacity; independent of a selected supply stack. */
      kind: 'phase-capacity';
      phaseSequence: number;
      metric: 'cumulative' | 'addition';
      powerBasis?: 'it-load' | 'facility';
    }
  | {
      kind: 'phase-feasibility';
      phaseSequence: number;
      phaseLabel: string;
      metric:
        | 'facility-load'
        | 'required-firm'
        | 'firm-deliverable'
        | 'firm-margin'
        | 'source-capacity'
        | 'source-firm';
      /** Human component label, required for per-source metrics. */
      componentLabel?: string;
    }
  | {
      /**
       * Remaining firm-deliverable capacity after the report's explicit N-1
       * contingency.  This is deliberately not interchangeable with an
       * authored firm-capacity target or an N-0 phase-feasibility result.
      */
      kind: 'firm-capacity-n1';
      metric:
        | 'largest-contingency'
        | 'firm-deliverable-n1'
        | 'required-firm-n1'
        | 'shortfall-n1';
    };

/**
 * One logical fact, reconciled across every section-scoped slot that can
 * carry it. Keyed by a cross-section `factId` (e.g. `'power.declaredMw'`,
 * `'electricUtility.name'`), NOT a `<sectionId>.<field>` path.
 */
export interface CanonicalFact {
  /**
   * Logical fact id, stable + cross-section. e.g. `'power.declaredMw'`,
   * `'electricUtility.name'`, `'siteArea.acres'`. NEVER section-scoped.
   */
  factId: string;
  /** Human label for the prompt line (e.g. "Electric utility"). */
  label: string;
  /** The reconciled value, or `null` when `status === 'absent'`. */
  value: unknown | null;
  /** Display unit when numeric (`'MW'` | `'MVA'` | `'acres'` | `'km'` | …). */
  unit?: string;
  /** Honesty grade — gates whether prose may assert this as fact. */
  status: FactStatus;
  /** Winning tier (1/2/3), or `null` when `status === 'absent'`. */
  resolvedFromTier: ResolverTier | null;
  /**
   * Every candidate source that competed (winner first), reusing the
   * resolver `SourceRef` shape so provenance is uniform across the layer.
   */
  sources: SourceRef[];
  /**
   * Cross-source disagreements surfaced (NOT dropped). Populated from
   * `reconcileTierSources`' own conflict detection; a tier-1-vs-tier-1
   * disagreement is the dangerous class (e.g. parcel.acres ≠
   * siteProfile.acres) and ALSO downgrades status to `'estimated'`.
   */
  conflicts?: ConflictDetail[];
  /**
   * Alternate-basis sibling label so the prompt can disambiguate
   * multi-basis facts (power triplet: declared / IT-load / facility / MVA;
   * cooling axes: strategy / generation / heat-rejection). Free-form.
   */
  basis?: string;
  /**
   * X-SED-6 — numeric bounds for a RANGE-valued fact (e.g. the developer's
   * "~700–1,100 acres under control" band). `value` carries the display
   * band string; these bounds enter the validator's numeric allow-set via
   * `numericIndex()` so a prose "1,100 acres" is matched to this fact
   * instead of mismatching against `siteArea.acres`. Optional + additive.
   */
  numericValues?: number[];
  /**
   * Human band label for a fact whose governed value is a DECLARED RANGE
   * ("Under 10 MW"), unit included. Set only by the declared-band fill.
   *
   * Carried so the validator can phrase a mismatch honestly. Without it arm
   * (iii) says "the only permitted value is 10 MW" for a project whose intent
   * is a band — text that reaches the repair prompt AND the operator's
   * one-click "canonical evidence value", which would write the scalar 10 into
   * the SED and break the "a range never collapses into a scalar" invariant.
   */
  bandDisplay?: string;
  /**
   * Value-kind stamped from the descriptor. `'categorical'` facts (flood
   * zone "X", zoning codes, cooling axes) are EXCLUDED from `entityIndex()`
   * — their short/common-word values ("X", "Low", "liquid") are not named
   * entities and would false-positive the entity gate's substring matching.
   * Optional for back-compat: absent ⇒ legacy behaviour (string values
   * index as entities).
   */
  valueKind?: 'numeric' | 'entity' | 'categorical';
  /**
   * Narrows where a numeric value is allowed to match.  Absent means the fact
   * keeps the existing global same-unit matching behaviour.
   */
  numericGroundingScope?: NumericGroundingScope;
}

/** A numeric entry in the validator's allow-set. */
export interface NumericFactIndexEntry {
  factId: string;
  value: number;
  /** Band label when this entry's fact governs a DECLARED RANGE, not a point. */
  bandDisplay?: string;
  unit?: string;
  status: FactStatus;
  numericGroundingScope?: NumericGroundingScope;
}

/** A named-entity entry in the validator's allow-set. */
export interface EntityFactIndexEntry {
  factId: string;
  label: string;
  value: string;
  status: FactStatus;
}

/**
 * The assembled store. `facts` is keyed by `factId`. The store is a plain
 * data object plus a few read helpers — it carries no behaviour beyond
 * lookups and index projections, so it serialises cleanly for telemetry /
 * the audit panels.
 */
export interface CanonicalFactStore {
  siteId: string;
  reportType: string;
  /** The coverage mode the store was built under (drives the laundering guard). */
  coverageMode: 'US' | 'global-degraded' | 'us-territory-degraded' | 'tribal-degraded';
  /** All facts, keyed by `factId`. */
  facts: Map<string, CanonicalFact>;
  /**
   * Conflicts where a non-winning source disagrees with the winner,
   * surfaced for the prompt + audit. The tier-1-vs-tier-1 subset is the
   * dangerous class. Never silently dropped (audit G4).
   */
  unresolvedConflicts: ConflictDetail[];
  /**
   * Context-entity ALLOWLIST for the invented-entity NER (grounding
   * validator FIX 2): the site's own name, its address components, and every
   * entity-ish string the SED carries (substation / utility / G&T / IX /
   * reactor / pipeline-operator names, county-parish-state, research-named
   * places, document titles). The NER treats a prose proper noun grounded by
   * this list as CONTEXT, not a fabrication — without it the site's own name
   * ("Redhead Industrial Park") and its parish flag as hallucinations.
   * Optional + additive: absent ⇒ legacy behaviour (facts-only grounding).
   */
  contextEntities?: readonly string[];
  /** Lookup one fact by id. */
  get(factId: string): CanonicalFact | undefined;
  /** All numeric facts with a finite value — the validator's numeric allow-set. */
  numericIndex(): NumericFactIndexEntry[];
  /** All string-entity facts with a non-empty value — the validator's entity allow-set. */
  entityIndex(): EntityFactIndexEntry[];
}

/**
 * Construct a `CanonicalFactStore` from a pre-built fact list. Kept as a
 * factory so the store helpers close over the fact map exactly once and the
 * builder stays focused on resolution. Pure.
 */
export function createCanonicalFactStore(args: {
  siteId: string;
  reportType: string;
  coverageMode: CanonicalFactStore['coverageMode'];
  facts: CanonicalFact[];
  /** Optional NER allowlist (site name / address / SED entity strings). */
  contextEntities?: readonly string[];
}): CanonicalFactStore {
  const facts = new Map<string, CanonicalFact>();
  for (const fact of args.facts) {
    facts.set(fact.factId, fact);
  }

  const unresolvedConflicts: ConflictDetail[] = [];
  for (const fact of facts.values()) {
    if (fact.conflicts && fact.conflicts.length > 0) {
      unresolvedConflicts.push(...fact.conflicts);
    }
  }

  return {
    siteId: args.siteId,
    reportType: args.reportType,
    coverageMode: args.coverageMode,
    facts,
    unresolvedConflicts,
    ...(args.contextEntities && args.contextEntities.length > 0
      ? { contextEntities: args.contextEntities }
      : {}),
    get(factId: string): CanonicalFact | undefined {
      return facts.get(factId);
    },
    numericIndex(): NumericFactIndexEntry[] {
      const out: NumericFactIndexEntry[] = [];
      for (const fact of facts.values()) {
        if (fact.status === 'absent') continue;
        if (typeof fact.value === 'number' && Number.isFinite(fact.value)) {
          out.push({
            factId: fact.factId,
            value: fact.value,
            unit: fact.unit,
            status: fact.status,
            ...(fact.bandDisplay ? { bandDisplay: fact.bandDisplay } : {}),
            ...(fact.numericGroundingScope
              ? { numericGroundingScope: fact.numericGroundingScope }
              : {}),
          });
        } else if (fact.numericValues && fact.numericValues.length > 0) {
          // X-SED-6 — a range-valued fact (display band string) contributes
          // each finite BOUND to the allow-set, so a prose "1,100 acres"
          // matches the under-control band instead of mismatching the
          // surveyed siteArea.acres.
          for (const bound of fact.numericValues) {
            if (Number.isFinite(bound)) {
              out.push({
                factId: fact.factId,
                value: bound,
                unit: fact.unit,
                status: fact.status,
                ...(fact.bandDisplay ? { bandDisplay: fact.bandDisplay } : {}),
                ...(fact.numericGroundingScope
                  ? { numericGroundingScope: fact.numericGroundingScope }
                  : {}),
              });
            }
          }
        }
      }
      return out;
    },
    entityIndex(): EntityFactIndexEntry[] {
      const out: EntityFactIndexEntry[] = [];
      for (const fact of facts.values()) {
        if (fact.status === 'absent') continue;
        // Only NAMED-ENTITY facts index here. Categorical facts (flood zone
        // "X", zoning codes) would false-positive the entity gate's substring
        // matching, and a numeric-kind fact whose display value is a band
        // string ("700–1,100") is a number, not a name. Absent valueKind ⇒
        // legacy behaviour (index every non-empty string).
        if (fact.valueKind !== undefined && fact.valueKind !== 'entity') continue;
        if (typeof fact.value === 'string' && fact.value.trim() !== '') {
          out.push({ factId: fact.factId, label: fact.label, value: fact.value, status: fact.status });
        }
      }
      return out;
    },
  };
}
